import { useState } from "react";
import { useCompanyRoles, useUpdateRole, useCreateRole, useDeleteRole, PERMISSION_GROUPS, type CompanyRole } from "@/hooks/useCompanyRoles";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Loader2, Plus, ShieldAlert, Trash2, Users } from "lucide-react";

const ROLE_COLORS = [
  { value: "#DC2626", label: "Red" },
  { value: "#2563EB", label: "Blue" },
  { value: "#059669", label: "Green" },
  { value: "#D97706", label: "Amber" },
  { value: "#7C3AED", label: "Purple" },
  { value: "#0891B2", label: "Cyan" },
  { value: "#E11D48", label: "Rose" },
  { value: "#F97316", label: "Orange" },
];

export const RolesTab = () => {
  const { data: roles = [], isLoading } = useCompanyRoles();
  const updateRole = useUpdateRole();
  const createRole = useCreateRole();
  const deleteRole = useDeleteRole();

  const [editRole, setEditRole] = useState<CompanyRole | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#6B7280");
  const [newDescription, setNewDescription] = useState("");
  const [newBaseLevel, setNewBaseLevel] = useState("4");
  const [copyFromId, setCopyFromId] = useState("");

  const [editPerms, setEditPerms] = useState<Record<string, boolean>>({});
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");
  const [editDescription, setEditDescription] = useState("");

  const openEdit = (role: CompanyRole) => {
    setEditRole(role);
    setEditPerms({ ...role.permissions });
    setEditName(role.name);
    setEditColor(role.color);
    setEditDescription(role.description || "");
  };

  const handleSaveRole = async () => {
    if (!editRole) return;
    try {
      await updateRole.mutateAsync({
        id: editRole.id,
        name: editName,
        color: editColor,
        description: editDescription,
        permissions: editPerms,
      });
      toast.success("Role updated");
      setEditRole(null);
    } catch (err: any) {
      toast.error(err.message || "Failed to update role");
    }
  };

  const handleCreateRole = async () => {
    if (!newName.trim()) { toast.error("Name is required"); return; }
    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");

      let perms: Record<string, boolean> = {};
      if (copyFromId) {
        const source = roles.find((r) => r.id === copyFromId);
        perms = source ? { ...source.permissions } : {};
      } else {
        PERMISSION_GROUPS.forEach((g) => g.keys.forEach((k) => { perms[k.key] = false; }));
        perms.view_projects = true;
      }

      await createRole.mutateAsync({
        company_id: companyId,
        name: newName,
        base_level: parseInt(newBaseLevel) || 4,
        color: newColor,
        description: newDescription || undefined,
        permissions: perms,
      });
      toast.success("Role created");
      setCreateOpen(false);
      setNewName("");
      setNewDescription("");
      setCopyFromId("");
    } catch (err: any) {
      toast.error(err.message || "Failed to create role");
    }
  };

  const handleDeleteRole = async (role: CompanyRole) => {
    if (role.is_system_role) { toast.error("System roles cannot be deleted"); return; }
    if (role.user_count && role.user_count > 0) { toast.error("Cannot delete role with active users"); return; }
    try {
      await deleteRole.mutateAsync(role.id);
      toast.success("Role deleted");
    } catch (err: any) { toast.error(err.message || "Failed to delete"); }
  };

  const togglePerm = (key: string) => {
    setEditPerms((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  }

  const systemRoles = roles.filter((r) => r.is_system_role);
  const customRoles = roles.filter((r) => !r.is_system_role);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Roles</h2>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-1" /> Create Role
        </Button>
      </div>

      <div className="space-y-2">
        {systemRoles.map((role) => (
          <RoleRow key={role.id} role={role} onEdit={() => openEdit(role)} isAdmin={role.name === "super_admin"} />
        ))}
        {customRoles.length > 0 && <Separator className="my-3" />}
        {customRoles.map((role) => (
          <RoleRow key={role.id} role={role} onEdit={() => openEdit(role)} onDelete={() => handleDeleteRole(role)} />
        ))}
      </div>

      {/* Edit Role Sheet */}
      <Sheet open={!!editRole} onOpenChange={(open) => !open && setEditRole(null)}>
        <SheetContent className="w-[420px] overflow-y-auto">
          {editRole && (
            <>
              <SheetHeader><SheetTitle>Edit Role — {editRole.name}</SheetTitle></SheetHeader>
              <div className="space-y-4 mt-4">
                {editRole.is_system_role && (
                  <Alert>
                    <ShieldAlert className="h-4 w-4" />
                    <AlertDescription>Changing system role permissions affects all users with this role.</AlertDescription>
                  </Alert>
                )}

                <div>
                  <Label>Role Name</Label>
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)} disabled={editRole.name === "super_admin"} />
                </div>

                <div>
                  <Label>Color</Label>
                  <div className="flex gap-2 mt-1">
                    {ROLE_COLORS.map((c) => (
                      <button key={c.value} className={`h-6 w-6 rounded-full border-2 transition-all ${editColor === c.value ? "border-foreground scale-110" : "border-transparent"}`} style={{ backgroundColor: c.value }} onClick={() => setEditColor(c.value)} title={c.label} />
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Description</Label>
                  <Input value={editDescription} onChange={(e) => setEditDescription(e.target.value)} />
                </div>

                <Separator />

                <p className="text-sm font-semibold">Permissions</p>
                {PERMISSION_GROUPS.map((group) => (
                  <div key={group.label}>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{group.label}</p>
                    <div className="space-y-2">
                      {group.keys.map((perm) => (
                        <div key={perm.key} className="flex items-center justify-between">
                          <Label className="font-normal text-sm">{perm.label}</Label>
                          <Switch checked={!!editPerms[perm.key]} onCheckedChange={() => togglePerm(perm.key)} disabled={editRole.name === "super_admin"} />
                        </div>
                      ))}
                    </div>
                    <Separator className="mt-3" />
                  </div>
                ))}

                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setEditRole(null)}>Cancel</Button>
                  <Button className="flex-1" onClick={handleSaveRole} disabled={updateRole.isPending}>
                    {updateRole.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Save Role
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Create Role Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create New Role</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Role Name *</Label><Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Site Superintendent" /></div>
            <div>
              <Label>Color</Label>
              <div className="flex gap-2 mt-1">
                {ROLE_COLORS.map((c) => (
                  <button key={c.value} className={`h-6 w-6 rounded-full border-2 transition-all ${newColor === c.value ? "border-foreground scale-110" : "border-transparent"}`} style={{ backgroundColor: c.value }} onClick={() => setNewColor(c.value)} />
                ))}
              </div>
            </div>
            <div><Label>Level (1=highest)</Label>
              <Select value={newBaseLevel} onValueChange={setNewBaseLevel}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{[1,2,3,4,5,6].map((n) => <SelectItem key={n} value={String(n)}>Level {n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Description</Label><Input value={newDescription} onChange={(e) => setNewDescription(e.target.value)} /></div>
            <div>
              <Label>Copy permissions from</Label>
              <Select value={copyFromId} onValueChange={setCopyFromId}>
                <SelectTrigger><SelectValue placeholder="Start from scratch" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Start from scratch</SelectItem>
                  {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateRole} disabled={createRole.isPending}>
              {createRole.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Create Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const RoleRow = ({ role, onEdit, onDelete, isAdmin }: { role: CompanyRole; onEdit: () => void; onDelete?: () => void; isAdmin?: boolean }) => (
  <Card className="hover:bg-accent/5 transition-colors">
    <CardContent className="flex items-center gap-4 py-3 px-4">
      <span className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: role.color }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium">{role.name}</p>
          <Badge variant="outline" className="text-[10px]">Level {role.base_level}</Badge>
          {role.is_system_role && <Badge variant="secondary" className="text-[10px]">System</Badge>}
        </div>
        {role.description && <p className="text-xs text-muted-foreground truncate">{role.description}</p>}
      </div>
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Users className="h-3.5 w-3.5" />
        <span>{role.user_count || 0}</span>
      </div>
      <div className="flex gap-1">
        <Button variant="outline" size="sm" onClick={onEdit}>
          {isAdmin ? "View" : "Edit"}
        </Button>
        {onDelete && !role.is_system_role && (
          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={onDelete}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </CardContent>
  </Card>
);
